import { useSelector } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import Loadable from "components/Loadable/Loadable";
import { lazy } from "react";
import { Route, Routes } from "react-router";
import themes from "assets/themes";
import NavigationScroll from "components/NavigationScroll/NavigationScroll";
import MainLayout from "components/MainLayout/MainLayout";
import ProtectedRoutes from "./utils/protectedroutes";
import { GenderProvider } from "./contexts/GenderContext";
import { GenderMatchProbProvider } from "./contexts/GenderMatchProbContext";
import { GenderRankingProbProvider } from "./contexts/GenderRankingProbContext";
import { SearchProvider } from "./contexts/SearchContext";
import { MatchProbabilitiesSearchProvider } from "./contexts/MatchProbabilitiesSearchContext";
import { SelectedDateProvider } from "./contexts/SelectedDateContext";
import { SelectedDateMatchProbProvider } from "./contexts/SelectedDateMatchProbContext";
import { ConfederationProvider } from "./contexts/ConfederationContext";
import { EventProvider } from "./contexts/EventContext";
import { CategoryProvider } from "./contexts/CategoryContext";
import { MWFProvider } from "./contexts/MWFContext";
import { ResultProvider } from "./contexts/ResultContext";
import TeamDetailLive from "../src/pages/teamDetailLive";
import "assets/fonts/fonts.css";

const Licenses = Loadable(lazy(() => import("./pages/licenses")));
const WorldRanking = Loadable(lazy(() => import("../src/pages/worldRanking")));
const HealthCheck = Loadable(lazy(() => import("../src/pages/healthCheck")));
const RankingExplained = Loadable(
  lazy(() => import("./pages/rankingExplained"))
);
const MatchProbabilities = Loadable(
  lazy(() => import("../src/pages/matchProbabilities"))
);
const RankingProbailites = Loadable(
  lazy(() => import("../src/pages/rankingProbabilities"))
);
const AuthLogin3 = Loadable(lazy(() => import("./pages/authentication")));
const Premium = Loadable(lazy(() => import("./pages/premium")));

// ==============================|| APP ||============================== //

const App = () => {
  const customization = useSelector((state) => state.customization);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <GenderProvider>
          <GenderMatchProbProvider>
            <GenderRankingProbProvider>
              <SelectedDateProvider>
                <SelectedDateMatchProbProvider>
                  <ConfederationProvider>
                    <EventProvider>
                      <CategoryProvider>
                        <MWFProvider>
                          <ResultProvider>
                            <NavigationScroll>
                              <Routes>
                                <Route element={<ProtectedRoutes />}>
                                  <Route
                                    path="/login"
                                    element={<AuthLogin3 />}
                                  />

                                  <Route
                                    path="/"
                                    element={
                                      <MainLayout>
                                        <SearchProvider>
                                          <WorldRanking />
                                        </SearchProvider>
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    path="/world-ranking"
                                    element={
                                      <MainLayout>
                                        <SearchProvider>
                                          <WorldRanking />
                                        </SearchProvider>
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    path="/team-details-live/men/:teamName"
                                    element={
                                      <MainLayout>
                                        <TeamDetailLive gender={1} />
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    path="/team-details-live/women/:teamName"
                                    element={
                                      <MainLayout>
                                        <TeamDetailLive gender={0} />
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    element={
                                      <ProtectedRoutes restricted={true} />
                                    }
                                  >
                                    <Route
                                      path="/ranking-probabilities"
                                      element={
                                        <MainLayout>
                                          <RankingProbailites />
                                        </MainLayout>
                                      }
                                    />
                                  </Route>
                                  <Route
                                    element={
                                      <ProtectedRoutes restricted={true} />
                                    }
                                  >
                                    <Route
                                      path="/health-check"
                                      element={
                                        <MainLayout>
                                          <HealthCheck />
                                        </MainLayout>
                                      }
                                    />
                                  </Route>

                                  <Route
                                    path="/licenses"
                                    element={
                                      <MainLayout>
                                        <Licenses />
                                      </MainLayout>
                                    }
                                  />
                                  <Route
                                    path="/premium"
                                    element={
                                      <MainLayout>
                                        <Premium />
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    path="/match-probabilities"
                                    element={
                                      <MainLayout>
                                        <MatchProbabilitiesSearchProvider>
                                          <MatchProbabilities />
                                        </MatchProbabilitiesSearchProvider>
                                      </MainLayout>
                                    }
                                  />

                                  <Route
                                    path="/ranking-explained"
                                    element={
                                      <MainLayout>
                                        <RankingExplained />
                                      </MainLayout>
                                    }
                                  />
                                </Route>
                              </Routes>
                            </NavigationScroll>
                          </ResultProvider>
                        </MWFProvider>
                      </CategoryProvider>
                    </EventProvider>
                  </ConfederationProvider>
                </SelectedDateMatchProbProvider>
              </SelectedDateProvider>
            </GenderRankingProbProvider>
          </GenderMatchProbProvider>
        </GenderProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;